import { useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import type { CostSection } from "./cost-breakdown-table";

export type QuoteSplitField = "labourCost" | "materialCost" | "plantCost";
const fields = { labour: "labourCost", materials: "materialCost", plant: "plantCost" } as const;
const labels = { labour: "Labour", materials: "Materials", plant: "Plant" } as const;

/** Existing whole-line allocations, not an invented per-unit material recipe. */
export function QuoteSectionAllowance({ section, item, index, symbol, onUpdate }: {
  section: CostSection;
  item: { labourCost?: number; materialCost?: number; plantCost?: number };
  index: number;
  symbol: string;
  onUpdate?: (field: QuoteSplitField, value: number) => void;
}) {
  const field = fields[section];
  const label = labels[section];
  const amount = item[field] ?? 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(amount));
  const valid = draft.trim() !== "" && Number.isFinite(Number(draft)) && Number(draft) >= 0;
  const formatted = `${symbol}${amount.toFixed(2)}`;
  return <div data-testid={`quote-section-allowance-${index}`}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <span className="text-sm font-medium">{label} · whole line</span>
      {!editing && onUpdate && <Button type="button" variant="outline" size="sm"
        onClick={() => { setDraft(String(amount)); setEditing(true); }}
        aria-label={`Edit ${label.toLowerCase()} for item ${index + 1}`}>
        <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
      </Button>}
    </div>
    {editing ? <div className="space-y-3">
      <label className="text-xs text-muted-foreground" htmlFor={`quote-allowance-${index}`}>{label} total ({symbol})</label>
      <Input id={`quote-allowance-${index}`} type="number" min="0" step="0.01" value={draft}
        onChange={e => setDraft(e.target.value)} aria-label={`${label} cost for item ${index + 1}`} />
      <p className="text-xs text-muted-foreground">For the whole line, not per unit. Saving updates this item’s quoted price; the other cost sections stay unchanged.</p>
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={!valid} onClick={() => {
          onUpdate?.(field, Math.round(Number(draft) * 100) / 100); setEditing(false);
        }}>Save</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div> : <div className="border rounded-md overflow-hidden text-sm" role="table" aria-label={`${label} cost`}>
      <div role="row" className="flex justify-between border-b px-4 py-3 text-muted-foreground">
        <span role="columnheader">Item</span><span role="columnheader">Cost</span>
      </div>
      <div role="row" className="flex justify-between border-b px-4 py-4">
        <span role="cell">{label}</span><span role="cell">{formatted}</span>
      </div>
      <div role="row" className="flex justify-between px-4 py-4 font-medium">
        <span role="cell">{label} total</span><span role="cell">{formatted}</span>
      </div>
    </div>}
  </div>;
}
